/**
 * LivingLogoIcon — Animated brand icon for interactive elements
 * 
 * The "living" version of Supabank logo with:
 * - Crypto Mint (#10B77F) energy flow through the zigzag glyph
 * - LED heartbeat indicator (#FF2A00) - micro dot that blinks every 5s
 * 
 * Variants:
 * - 'dark': Blue glyph on dark backgrounds (with glow)
 * - 'light': Obsidian glyph on light backgrounds
 * - 'inverted': WHITE glyph for colored backgrounds (e.g., blue buttons)
 * 
 * Use this for:
 * - Chat/Assistant buttons (variant='inverted' on blue bg)
 * - Dashboard headers (variant='dark' on dark bg)
 * - Loading states
 * - Hero sections
 * 
 * @see docs/brand-book/01-IDENTITY.md - Living Brand Philosophy
 * @see src/components/canon/LivingBrandShowcase.tsx - Full showcase
 */

import { cn } from '@/lib/utils';
import React from 'react';

// Brand colors from Brand Book SSOT (/design/canon)
const BRAND_COLORS = {
  supabankBlue: '#3B82F6',
  cryptoMint: '#10B77F',
  ledRed: '#FF2A00',
  ledGlow: 'rgba(255, 42, 0, 0.6)',
  obsidian: '#0D1F1A',
  light: '#F2F4F6',
  white: '#FFFFFF',
} as const;

// Official zigzag glyph path
const GLYPH_PATH = 'M 196 48 L 72 112 L 184 144 L 60 208';

// LED position - bottom right corner, near end of glyph
const LED_POSITION = { cx: 210, cy: 220 };

export interface LivingLogoIconProps {
  /** Size of the icon in pixels */
  size?: number;
  /** 
   * Visual variant:
   * - 'dark': Blue glyph for dark backgrounds
   * - 'light': Obsidian glyph for light backgrounds  
   * - 'inverted': WHITE glyph for colored backgrounds (buttons, etc)
   */
  variant?: 'light' | 'dark' | 'inverted';
  /** Show LED heartbeat indicator (default: true) */
  showLed?: boolean;
  /** Show energy flow animation (Crypto Mint) */
  showEnergyFlow?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * LivingLogoIcon - Compact animated logo for buttons and headers
 * 
 * Default: shows both energy flow AND LED heartbeat for full Living Brand effect
 */
export const LivingLogoIcon: React.FC<LivingLogoIconProps> = ({
  size = 24,
  variant = 'dark',
  showLed = true,
  showEnergyFlow = true,
  className,
}) => {
  // Generate unique IDs for filters (avoid conflicts when multiple instances)
  const uniqueId = React.useId();
  const glowFilterId = `living-glow-${uniqueId}`;
  const ledGlowFilterId = `led-glow-${uniqueId}`;
  const glyphId = `glyph-${uniqueId}`;

  // Determine stroke color based on variant
  const getStrokeColor = () => {
    switch (variant) {
      case 'inverted':
        return BRAND_COLORS.white;
      case 'light':
        return BRAND_COLORS.obsidian;
      case 'dark':
      default:
        return BRAND_COLORS.supabankBlue;
    }
  };

  const strokeColor = getStrokeColor();
  const useGlow = variant === 'dark' || variant === 'inverted';

  return (
    <svg
      viewBox="0 0 256 256"
      width={size}
      height={size}
      className={cn('overflow-visible flex-shrink-0', className)}
      aria-label="Supabank"
      role="img"
    >
      <defs>
        <path id={glyphId} d={GLYPH_PATH} />

        {/* Crypto Mint Glow Filter */}
        <filter id={glowFilterId} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* LED Glow Filter */}
        {showLed && (
          <filter id={ledGlowFilterId} x="-200%" y="-200%" width="500%" height="500%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        )}
      </defs>

      {/* Base Glyph */}
      <use
        href={`#${glyphId}`}
        stroke={strokeColor}
        strokeWidth={28}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ filter: useGlow ? `url(#${glowFilterId})` : 'none' }}
      />

      {/* Crypto Mint Energy Flow */}
      {showEnergyFlow && (
        <use
          href={`#${glyphId}`}
          stroke={BRAND_COLORS.cryptoMint}
          strokeWidth={6}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="80 600"
          filter={useGlow ? `url(#${glowFilterId})` : 'none'}
          className="animate-energy-flow"
        />
      )}

      {/* LED Heartbeat - micro dot at bottom right, blinks every 5s */}
      {showLed && (
        <g className="animate-led-heartbeat">
          <circle
            cx={LED_POSITION.cx}
            cy={LED_POSITION.cy}
            r={4}
            fill={BRAND_COLORS.ledRed}
            filter={`url(#${ledGlowFilterId})`}
          />
        </g>
      )}

      {/* Inline styles for animations */}
      <style>{`
        @keyframes energy-flow {
          0% { stroke-dashoffset: 680; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { stroke-dashoffset: -680; opacity: 0; }
        }
        .animate-energy-flow {
          animation: energy-flow 3s cubic-bezier(0.4, 0, 0.2, 1) infinite;
        }

        @keyframes led-heartbeat {
          0%, 89% { opacity: 0; transform: scale(0.8); }
          90% { opacity: 1; transform: scale(1.3); }
          92% { opacity: 0.4; transform: scale(1); }
          94% { opacity: 1; transform: scale(1.2); }
          96% { opacity: 0.6; transform: scale(1); }
          100% { opacity: 0; transform: scale(0.8); }
        }
        .animate-led-heartbeat {
          animation: led-heartbeat 5s ease-in-out infinite;
          transform-origin: ${LED_POSITION.cx}px ${LED_POSITION.cy}px;
        }
      `}</style>
    </svg>
  );
};

/**
 * LivingLogoIconCompact - Extra small version for tight spaces (nav items, badges)
 * No LED, smaller energy flow
 */
export const LivingLogoIconCompact: React.FC<{
  size?: number;
  variant?: 'light' | 'dark' | 'inverted';
  className?: string;
}> = ({ size = 20, variant = 'dark', className }) => {
  return (
    <LivingLogoIcon
      size={size}
      variant={variant}
      showLed={false}
      showEnergyFlow={true}
      className={className}
    />
  );
};

export default LivingLogoIcon;
